/**
 * 🧪 QuizEditor.js - Create/Edit Quiz Form
 * * Multi-step form for creating quizzes and practices:
 * Step 1: Metadata (type, project, difficulty, title, description)
 * Step 2: Tasks (10 tasks with audio, spectrogram, waveform, options)
 * Step 3: Review & Save
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../firebase-config'; // ✅ FIXED PATH
import './QuizEditor.css';

// ============================================================================
// CONSTANTS
// ============================================================================

const PROJECT_OPTIONS = [
  { value: 'whale', label: '🐋 Whale Chat' },
  { value: 'dolphin', label: '🐬 Dolphin Chat' },
  { value: 'manatee', label: '🦭 Manatee Chat' },
];

const DIFFICULTY_OPTIONS = [
  { value: 'easy', label: '🟢 Easy' },
  { value: 'medium', label: '🟡 Medium' },
  { value: 'hard', label: '🔴 Hard' },
];

// Project-specific labels (same as annotations)
const PROJECT_LABELS = {
  whale: [
    'No sound',
    'Whale - Humpback',
    'Whale - Blue',
    'Whale - Fin',
    'Whale - Sperm',
    'Whale - Unknown',
    'Chorusing',
    'Dolphin',
    'Boat/Ship',
    'Unknown biological',
    'Unknown non-biological',
    'Noise/Static',
  ],
  dolphin: [
    'No sound',
    'Dolphin - Whistle',
    'Dolphin - Click',
    'Dolphin - Burst pulse',
    'Dolphin - Unknown',
    'Snapping Shrimp',
    'Whale',
    'Fish sounds',
    'Boat/Ship',
    'Unknown biological',
    'Unknown non-biological',
    'Noise/Static',
  ],
  manatee: [
    'No sound',
    'Manatee - Vocalization',
    'Manatee - Feeding',
    'Manatee - Unknown',
    'Fish sounds',
    'Snapping shrimp',
    'Boat/Ship',
    'Unknown biological',
     'Unknown non-biological',
    'Noise/Static',
  ],
};

const TASK_COUNT = 10;

const EMPTY_TASK = {
  order: 1,
  mediaType: 'audio',             // 'audio' or 'video'
  audioFile: null,
  spectrogramFile: null,
  waveformFile: null,
  audioKey: '',
  spectrogramKey: '',
  waveformKey: '',
  videoUrl: '',                   // Direct S3 URL for video tasks
  prompt: 'What do you hear in this recording?',
  options: [],
  correctAnswers: [],
  hasHowMany: false,
  correctHowMany: 1,
  explanation: '',
  explanationImageFile: null,
  explanationImageKey: '',
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const QuizEditor = ({ quiz, onClose, onSave }) => {
  const functions = getFunctions();
  
  // Form state
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});

  // Step 1: Metadata
  const [type, setType] = useState(quiz?.type || 'quiz');
  const [project, setProject] = useState(quiz?.project || 'whale');
  const [difficulty, setDifficulty] = useState(quiz?.difficulty || 'easy');
  const [title, setTitle] = useState(quiz?.title || '');
  const [description, setDescription] = useState(quiz?.description || '');
  const [icon, setIcon] = useState(quiz?.icon || '🧪');

  // Step 2: Tasks
  const [tasks, setTasks] = useState(() => {
    if (quiz?.tasks) {
      return quiz.tasks.map((t, i) => ({
        ...EMPTY_TASK,
        ...t,
        order: i + 1,
      }));
    }
    return Array.from({ length: TASK_COUNT }, (_, i) => ({
      ...EMPTY_TASK,
      order: i + 1,
      options: [...PROJECT_LABELS.whale],
    }));
  });

  const [currentTaskIndex, setCurrentTaskIndex] = useState(0);

  // Update options when project changes
  useEffect(() => {
    if (!quiz) {
      setTasks((prev) =>
        prev.map((task) => ({
          ...task,
          options: [...PROJECT_LABELS[project]],
          correctAnswers: [],
        }))
      );
    }
  }, [project, quiz]);

  // =========================================================================
  // TASK HANDLERS
  // =========================================================================

  const updateTask = (index, field, value) => {
    setTasks((prev) =>
      prev.map((task, i) =>
        i === index ? { ...task, [field]: value } : task
      )
    );
  };

  const handleFileSelect = async (index, fileType, file) => {
    if (!file) return;

    updateTask(index, `${fileType}File`, file);
    
    // Generate preview URL
    const previewUrl = URL.createObjectURL(file);
    updateTask(index, `${fileType}Preview`, previewUrl);
  };

  const toggleCorrectAnswer = (taskIndex, answer) => {
    setTasks((prev) =>
      prev.map((task, i) => {
        if (i !== taskIndex) return task;
        
        const isSelected = task.correctAnswers.includes(answer);
        const newAnswers = isSelected
          ? task.correctAnswers.filter((a) => a !== answer)
          : [...task.correctAnswers, answer];
        
        return { ...task, correctAnswers: newAnswers };
      })
    );
  };

  // =========================================================================
  // UPLOAD FILES TO S3
  // =========================================================================

  const uploadFile = async (quizId, file, folder, filename) => {
    if (!file) return null;

    try {
      // Get presigned URL from Cloud Function
      const getPresignedUrl = httpsCallable(functions, 'getS3PresignedUrl');
      const result = await getPresignedUrl({
        bucket: 'cetalinguastations',
        key: `quiz/${quizId}/${folder}/${filename}`,
        contentType: file.type,
        operation: 'putObject',
      });

      const { url } = result.data;

      // Upload to S3
      const response = await fetch(url, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      return `${folder}/${filename}`;
    } catch (error) {
      console.error('Upload error:', error);
      throw error;
    }
  };

  const uploadAllTaskFiles = async (quizId) => {
    const updatedTasks = [...tasks];

    for (let i = 0; i < tasks.length; i++) {
      const task = tasks[i];
      const taskNum = String(i + 1).padStart(2, '0');

      setUploadProgress((prev) => ({ ...prev, [i]: 'uploading' }));

      try {
        // Video tasks don't need audio/spectrogram/waveform uploads
        if (task.mediaType === 'video') {
          updatedTasks[i].videoUrl = task.videoUrl || '';
        } else {
          // Upload audio
          if (task.audioFile) {
            const ext = task.audioFile.name.split('.').pop();
            const key = await uploadFile(quizId, task.audioFile, 'audio', `task_${taskNum}.${ext}`);
            updatedTasks[i].audioKey = key;
          }

          // Upload spectrogram
          if (task.spectrogramFile) {
            const key = await uploadFile(quizId, task.spectrogramFile, 'spectrograms', `task_${taskNum}.png`);
            updatedTasks[i].spectrogramKey = key;
          }

          // Upload waveform
          if (task.waveformFile) {
            const key = await uploadFile(quizId, task.waveformFile, 'waveforms', `task_${taskNum}_waveform.png`);
            updatedTasks[i].waveformKey = key;
          }
        }

        // Upload explanation image (for ALL task types — audio AND video)
        if (task.explanationImageFile) {
          const key = await uploadFile(quizId, task.explanationImageFile, 'explanations', `task_${taskNum}_explain.png`);
          updatedTasks[i].explanationImageKey = key;
        }

        setUploadProgress((prev) => ({ ...prev, [i]: 'done' }));
      } catch (error) {
        setUploadProgress((prev) => ({ ...prev, [i]: 'error' }));
        throw error;
      }
    }

    return updatedTasks;
  };

  // =========================================================================
  // SAVE QUIZ
  // =========================================================================

  const handleSave = async () => {
    setSaving(true);

    try {
      // Prepare quiz document
      const quizData = {
        type,
        project,
        difficulty,
        title,
        description,
        icon,
        taskCount: TASK_COUNT,
        status: 'draft',
        isActive: false,
        totalAttempts: quiz?.totalAttempts || 0,
        totalCompletions: quiz?.totalCompletions || 0,
        averageScore: quiz?.averageScore || 0,
        perfectScores: quiz?.perfectScores || 0,
        updatedAt: serverTimestamp(),
      };

      let quizId = quiz?.id;

      if (quiz) {
        // Update existing quiz
        const quizRef = doc(db, 'quizzes', quiz.id);
        await updateDoc(quizRef, quizData);
      } else {
        // Create new quiz
        quizData.createdAt = serverTimestamp();
        quizData.createdBy = 'admin'; // Replace with actual admin UID
        const docRef = await addDoc(collection(db, 'quizzes'), quizData);
        quizId = docRef.id;
      }

      // Upload files and get updated tasks with S3 keys
      const updatedTasks = await uploadAllTaskFiles(quizId);

      // Clean task data for Firestore (remove File objects)
      const cleanTasks = updatedTasks.map((task) => ({
        order: task.order,
        mediaType: task.mediaType || 'audio',
        audioKey: task.audioKey,
        spectrogramKey: task.spectrogramKey,
        waveformKey: task.waveformKey,
        videoUrl: task.videoUrl || '',
        prompt: task.prompt,
        options: task.options,
        correctAnswers: task.correctAnswers,
        hasHowMany: task.hasHowMany,
        correctHowMany: task.correctHowMany,
        explanation: task.explanation,
        explanationImageKey: task.explanationImageKey,
      }));

      // Update quiz with tasks
      const quizRef = doc(db, 'quizzes', quizId);
      await updateDoc(quizRef, { tasks: cleanTasks });

      alert('Quiz saved successfully!');
      onSave();
    } catch (error) {
      console.error('Save error:', error);
      alert('Failed to save: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  // =========================================================================
  // VALIDATION
  // =========================================================================

  const validateStep1 = () => {
    return title.trim() && description.trim();
  };

  const validateStep2 = () => {
    return tasks.every(
      (task) => {
        // Must have at least one correct answer
        if (task.correctAnswers.length === 0) return false;
        
        if (task.mediaType === 'video') {
          // Video tasks need a video URL
          return task.videoUrl && task.videoUrl.trim().length > 0;
        } else {
          // Audio tasks need audio file + spectrogram
          return (
            (task.audioFile || task.audioKey) &&
            (task.spectrogramFile || task.spectrogramKey)
          );
        }
      }
    );
  };

  // =========================================================================
  // RENDER STEP 1: METADATA
  // =========================================================================

  const renderStep1 = () => (
    <div className="qe-step">
      <h3>📋 Quiz Details</h3>

      <div className="qe-row">
        <div className="qe-field">
          <label>Type</label>
          <div className="qe-radio-group">
            <label className={type === 'quiz' ? 'active' : ''}>
              <input
                type="radio"
                value="quiz"
                checked={type === 'quiz'}
                onChange={(e) => setType(e.target.value)}
              />
              <span className="qe-radio-content">
                <span className="qe-radio-icon">📝</span>
                <span className="qe-radio-label">Quiz</span>
                <span className="qe-radio-desc">No feedback until end</span>
              </span>
            </label>
            <label className={type === 'practice' ? 'active' : ''}>
              <input
                type="radio"
                value="practice"
                checked={type === 'practice'}
                onChange={(e) => setType(e.target.value)}
              />
              <span className="qe-radio-content">
                <span className="qe-radio-icon">📖</span>
                <span className="qe-radio-label">Practice</span>
                <span className="qe-radio-desc">Instant feedback</span>
              </span>
            </label>
          </div>
        </div>
      </div>

      <div className="qe-row">
        <div className="qe-field">
          <label>Project</label>
          <select value={project} onChange={(e) => setProject(e.target.value)}>
            {PROJECT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        <div className="qe-field">
          <label>Difficulty</label>
          <select value={difficulty} onChange={(e) => setDifficulty(e.target.value)}>
            {DIFFICULTY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        <div className="qe-field qe-field-small">
          <label>Icon</label>
          <input
            type="text"
            value={icon}
            onChange={(e) => setIcon(e.target.value)}
            maxLength={2}
            placeholder="🧪"
          />
        </div>
      </div>

      <div className="qe-field">
        <label>Title</label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g., Whale Song Basics"
          maxLength={100}
        />
      </div>

      <div className="qe-field">
        <label>Description</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Brief description of what users will learn..."
          rows={3}
          maxLength={500}
        />
      </div>
    </div>
  );

  // =========================================================================
  // RENDER STEP 2: TASKS
  // =========================================================================

  const renderStep2 = () => {
    const task = tasks[currentTaskIndex];

    return (
      <div className="qe-step">
        <h3>🎯 Task {currentTaskIndex + 1} of {TASK_COUNT}</h3>

        {/* Task Navigation */}
        <div className="qe-task-nav">
          {tasks.map((t, i) => {
            const hasMedia = t.mediaType === 'video'
              ? (t.videoUrl && t.videoUrl.trim().length > 0)
              : (t.audioFile || t.audioKey);
            const isComplete = t.correctAnswers.length > 0 && hasMedia;
            const isCurrent = i === currentTaskIndex;
            
            return (
              <button
                key={i}
                className={`qe-task-dot ${isCurrent ? 'current' : ''} ${isComplete ? 'complete' : ''}`}
                onClick={() => setCurrentTaskIndex(i)}
              >
                {i + 1}
              </button>
            );
          })}
        </div>

        {/* Media Type Toggle */}
        <div className="qe-field">
          <label>Media Type</label>
          <div className="qe-media-toggle">
            <button
              className={`qe-media-btn ${(task.mediaType || 'audio') === 'audio' ? 'active' : ''}`}
              onClick={() => updateTask(currentTaskIndex, 'mediaType', 'audio')}
            >
              🔊 Audio + Images
            </button>
            <button
              className={`qe-media-btn ${task.mediaType === 'video' ? 'active' : ''}`}
              onClick={() => updateTask(currentTaskIndex, 'mediaType', 'video')}
            >
              🎬 Video
            </button>
          </div>
        </div>

        {/* File Uploads (Audio mode) */}
        {(task.mediaType || 'audio') === 'audio' && (
          <div className="qe-uploads">
            <div className="qe-upload-box">
              <label>🔊 Audio File *</label>
              <input
                key={`audio-${currentTaskIndex}`}
                type="file"
                accept="audio/*"
                onChange={(e) => handleFileSelect(currentTaskIndex, 'audio', e.target.files[0])}
              />
              {(task.audioFile || task.audioKey) && (
                <span className="qe-upload-status">✅ {task.audioFile?.name || task.audioKey}</span>
              )}
            </div>

            <div className="qe-upload-box">
              <label>📊 Spectrogram (PNG) *</label>
              <input
                key={`spectrogram-${currentTaskIndex}`}
                type="file"
                accept="image/png"
                onChange={(e) => handleFileSelect(currentTaskIndex, 'spectrogram', e.target.files[0])}
              />
              {(task.spectrogramFile || task.spectrogramKey) && (
                <span className="qe-upload-status">✅ Uploaded</span>
              )}
              {task.spectrogramPreview && (
                <img src={task.spectrogramPreview} alt="Spectrogram preview" className="qe-preview" />
              )}
            </div>

            <div className="qe-upload-box">
              <label>〰️ Waveform (PNG)</label>
              <input
                key={`waveform-${currentTaskIndex}`}
                type="file"
                accept="image/png"
                onChange={(e) => handleFileSelect(currentTaskIndex, 'waveform', e.target.files[0])}
              />
              {(task.waveformFile || task.waveformKey) && (
                <span className="qe-upload-status">✅ Uploaded</span>
              )}
              {task.waveformPreview && (
                <img src={task.waveformPreview} alt="Waveform preview" className="qe-preview" />
              )}
            </div>
          </div>
        )}

        {/* Video URL Input (Video mode) */}
        {task.mediaType === 'video' && (
          <div className="qe-video-input">
            <div className="qe-field">
              <label>🎬 Video URL * <span className="qe-hint">(S3 public link to .mp4 file)</span></label>
              <input
                type="text"
                value={task.videoUrl || ''}
                onChange={(e) => updateTask(currentTaskIndex, 'videoUrl', e.target.value)}
                placeholder="https://s3.amazonaws.com/cetalingua/wp-content/uploads/.../example.mp4"
              />
              {task.videoUrl && task.videoUrl.trim() && (
                <div className="qe-video-preview">
                  <span className="qe-upload-status">✅ Video URL set</span>
                  <video
                    src={task.videoUrl}
                    controls
                    className="qe-video-player"
                    onError={(e) => e.target.style.display = 'none'}
                  >
                    Your browser does not support the video tag.
                  </video>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Prompt */}
        <div className="qe-field">
          <label>Question Prompt</label>
          <input
            type="text"
            value={task.prompt}
            onChange={(e) => updateTask(currentTaskIndex, 'prompt', e.target.value)}
            placeholder="What do you hear in this recording?"
          />
        </div>

        {/* Answer Options */}
        <div className="qe-field">
          <label>Correct Answer(s) * <span className="qe-hint">(select one or more)</span></label>
          <div className="qe-options-grid">
            {task.options.map((option, i) => (
              <label
                key={i}
                className={`qe-option ${task.correctAnswers.includes(option) ? 'selected' : ''}`}
              >
                <input
                  type="checkbox"
                  checked={task.correctAnswers.includes(option)}
                  onChange={() => toggleCorrectAnswer(currentTaskIndex, option)}
                />
                <span>{option}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Explanation */}
        <div className="qe-field">
          <label>Explanation <span className="qe-hint">(shown in practice mode)</span></label>
          <textarea
            value={task.explanation}
            onChange={(e) => updateTask(currentTaskIndex, 'explanation', e.target.value)}
            placeholder="Explain why this is the correct answer..."
            rows={3}
          />
        </div>

        {/* Explanation Image */}
        <div className="qe-upload-box qe-upload-small">
          <label>📷 Explanation Image (optional)</label>
          <input
            key={`explanationImage-${currentTaskIndex}`}
            type="file"
            accept="image/*"
            onChange={(e) => handleFileSelect(currentTaskIndex, 'explanationImage', e.target.files[0])}
          />
          {(task.explanationImageFile || task.explanationImageKey) && (
            <span className="qe-upload-status">✅ {task.explanationImageFile?.name || task.explanationImageKey}</span>
          )}
          {task.explanationImagePreview && (
            <img src={task.explanationImagePreview} alt="Explanation preview" className="qe-preview" />
          )}
        </div>

        {/* Task Navigation Buttons */}
        <div className="qe-task-actions">
          <button
            className="qe-btn qe-btn-secondary"
            onClick={() => setCurrentTaskIndex(Math.max(0, currentTaskIndex - 1))}
            disabled={currentTaskIndex === 0}
          >
            ← Previous Task
          </button>
          <button
            className="qe-btn qe-btn-primary"
            onClick={() => setCurrentTaskIndex(Math.min(TASK_COUNT - 1, currentTaskIndex + 1))}
            disabled={currentTaskIndex === TASK_COUNT - 1}
          >
            Next Task →
          </button>
        </div>
      </div>
    );
  };

  // =========================================================================
  // RENDER STEP 3: REVIEW
  // =========================================================================

  const renderStep3 = () => {
    const completeTasks = tasks.filter((t) => t.correctAnswers.length > 0).length;
    const uploadedTasks = tasks.filter((t) => {
      if (t.mediaType === 'video') return t.videoUrl && t.videoUrl.trim().length > 0;
      return t.audioFile || t.audioKey;
    }).length;

    return (
      <div className="qe-step">
        <h3>✅ Review & Save</h3>

        <div className="qe-review-summary">
          <div className="qe-review-item">
            <span className="qe-review-label">Type:</span>
            <span className="qe-review-value">{type === 'quiz' ? '📝 Quiz' : '📖 Practice'}</span>
          </div>
          <div className="qe-review-item">
            <span className="qe-review-label">Project:</span>
            <span className="qe-review-value">{PROJECT_OPTIONS.find((p) => p.value === project)?.label}</span>
          </div>
          <div className="qe-review-item">
            <span className="qe-review-label">Difficulty:</span>
            <span className="qe-review-value">{DIFFICULTY_OPTIONS.find((d) => d.value === difficulty)?.label}</span>
          </div>
          <div className="qe-review-item">
            <span className="qe-review-label">Title:</span>
            <span className="qe-review-value">{title}</span>
          </div>
        </div>

        <div className="qe-review-tasks">
          <h4>Tasks Status</h4>
          <div className="qe-review-progress">
            <div className="qe-review-bar">
              <div
                className="qe-review-bar-fill"
                style={{ width: `${(completeTasks / TASK_COUNT) * 100}%` }}
              />
            </div>
            <span>{completeTasks} / {TASK_COUNT} tasks configured</span>
          </div>
          <div className="qe-review-progress">
            <div className="qe-review-bar">
              <div
                className="qe-review-bar-fill uploaded"
                style={{ width: `${(uploadedTasks / TASK_COUNT) * 100}%` }}
              />
            </div>
            <span>{uploadedTasks} / {TASK_COUNT} media files ready</span>
          </div>
        </div>

        {/* Task List Preview */}
        <div className="qe-task-list">
          {tasks.map((task, i) => (
            <div
              key={i}
              className={`qe-task-preview ${task.correctAnswers.length > 0 ? 'complete' : 'incomplete'}`}
            >
              <span className="qe-task-num">#{i + 1}</span>
              <span className="qe-task-answers">
                {task.correctAnswers.length > 0
                  ? task.correctAnswers.join(', ')
                  : 'No answer selected'}
              </span>
              <span className="qe-task-files">
                {task.mediaType === 'video' ? (
                  <>
                    {task.videoUrl && task.videoUrl.trim() ? '🎬' : '⚪'}
                  </>
                ) : (
                  <>
                    {task.audioFile || task.audioKey ? '🔊' : '⚪'}
                    {task.spectrogramFile || task.spectrogramKey ? '📊' : '⚪'}
                  </>
                )}
              </span>
            </div>
          ))}
        </div>

        {/* Upload Progress */}
        {saving && Object.keys(uploadProgress).length > 0 && (
          <div className="qe-upload-progress">
            <h4>Uploading Files...</h4>
            {tasks.map((_, i) => (
              <div key={i} className="qe-upload-item">
                <span>Task {i + 1}:</span>
                <span className={`qe-upload-status-${uploadProgress[i]}`}>
                  {uploadProgress[i] === 'uploading' && '⏳ Uploading...'}
                  {uploadProgress[i] === 'done' && '✅ Done'}
                  {uploadProgress[i] === 'error' && '❌ Error'}
                  {!uploadProgress[i] && '⏸️ Pending'}
                </span>
              </div>
            ))}
          </div>
        )}

        {!validateStep2() && (
          <div className="qe-warning">
            ⚠️ Some tasks are incomplete. Please ensure all tasks have:
            <ul>
              <li>At least one correct answer selected</li>
              <li><strong>Audio tasks:</strong> An audio file and spectrogram image uploaded</li>
              <li><strong>Video tasks:</strong> A valid video URL entered</li>
            </ul>
          </div>
        )}
      </div>
    );
  };

  // =========================================================================
  // MAIN RENDER
  // =========================================================================

  return (
    <div className="qe-container">
      {/* Header */}
      <div className="qe-header">
        <button className="qe-close" onClick={onClose}>←</button>
        <h2>{quiz ? 'Edit Quiz' : 'Create New Quiz'}</h2>
      </div>

      {/* Step Indicator */}
      <div className="qe-steps">
        <div className={`qe-step-indicator ${step >= 1 ? 'active' : ''}`}>
          <span className="qe-step-num">1</span>
          <span className="qe-step-label">Details</span>
        </div>
        <div className="qe-step-line" />
        <div className={`qe-step-indicator ${step >= 2 ? 'active' : ''}`}>
          <span className="qe-step-num">2</span>
          <span className="qe-step-label">Tasks</span>
        </div>
        <div className="qe-step-line" />
        <div className={`qe-step-indicator ${step >= 3 ? 'active' : ''}`}>
          <span className="qe-step-num">3</span>
          <span className="qe-step-label">Review</span>
        </div>
      </div>

      {/* Step Content */}
      <div className="qe-content">
        {step === 1 && renderStep1()}
        {step === 2 && renderStep2()}
        {step === 3 && renderStep3()}
      </div>

      {/* Footer */}
      <div className="qe-footer">
        <button className="qe-btn qe-btn-secondary" onClick={onClose}>
          Cancel
        </button>

        <div className="qe-footer-right">
          {step > 1 && (
            <button
              className="qe-btn qe-btn-secondary"
              onClick={() => setStep(step - 1)}
            >
              ← Back
            </button>
          )}

          {step < 3 ? (
            <button
              className="qe-btn qe-btn-primary"
              onClick={() => setStep(step + 1)}
              disabled={step === 1 && !validateStep1()}
            >
              Next →
            </button>
          ) : (
            <button
              className="qe-btn qe-btn-success"
              onClick={handleSave}
              disabled={saving || !validateStep2()}
            >
              {saving ? '⏳ Saving...' : '💾 Save Quiz'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuizEditor;